import { Asset, Entry } from "contentful"
import React from "react"
import { Layout } from "../../components/Layout/Layout"
import { TypeCreatorSkeleton, TypeProductEntitySkeleton } from "../../lib/contentful/ContentfulVzdelanibudoucnostiTypes"
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsResult } from "next"
import { contentfulVzClient } from "../../lib/contentful/contentfulVzClient"
import { ContentfulVzContentType } from "../../lib/contentful/ContentfulVzContentType"
import { dev } from "../../lib/dev"
import { NextSeo } from "next-seo"
import { webUrl } from "../../config/web"
import { SinglePage } from "../../components/SinglePage/SinglePage"
import { Avatar, Breadcrumb, Col, Row, Typography } from "antd"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { documentToRenderOptions } from "../../components/Layout/documentToRenderOptions"
import { CoursesList } from "../../components/CoursesList/CoursesList"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import Head from "next/head"
import { css } from "@emotion/react"
import Link from "next/link"

const trimText = (text: string, maxLength: number = 160 - 3): string => (text.length > maxLength ? text.slice(0, maxLength - 3) + "..." : text)

const LektoriSlu: React.FC<Props> = ({ lecturer, lecturerCourses }) => {
  if (!lecturer?.sys?.id) {
    return <>Nenalezeno</>
  }

  const {
    fields: { slug, bio, name, image, position, email }
  } = lecturer

  const imageAsset = image ? (image as Asset<undefined, string>) : undefined
  const seoDesc = trimText(documentToPlainTextString(bio))

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: name,
    jobTitle: position,
    email: email,
    image: imageAsset ? (imageAsset.fields.file.url.startsWith("http") ? imageAsset.fields.file.url : `https:${imageAsset.fields.file.url}`) : undefined,
    url: `${webUrl}/lektori/${slug}`,
    description: seoDesc
  }

  return (
    <>
      <NextSeo
        title={`${name} - ${position}`}
        description={seoDesc}
        canonical={`${webUrl}/lektori/${slug}`}
        openGraph={{
          url: `${webUrl}/lektori/${slug}`,
          title: `${name} - ${position}`,
          description: seoDesc,
          images: imageAsset
            ? [
                {
                  url: `https:${imageAsset.fields.file.url}`,
                  alt: imageAsset.fields.title || `${name} profilový obrázek`
                }
              ]
            : [],
          site_name: "Vzdelani Budoucnosti"
        }}
        twitter={{
          cardType: "summary_large_image"
        }}
        additionalMetaTags={[
          {
            name: "author",
            content: name
          }
        ]}
      />
      <Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }} />
      </Head>

      <Layout>
        <SinglePage title={`${name} - ${position}`} color={undefined} bgImage={undefined}>
          <Breadcrumb
            css={css`
              margin-top: 20px;
              margin-bottom: 20px;
            `}>
            <Breadcrumb.Item>Domů</Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link href={`/lektori`}>Lektoři</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link href={`/lektori/${slug}`}>{name}</Link>
            </Breadcrumb.Item>
          </Breadcrumb>

          <Row gutter={[30, 30]} style={{ marginTop: 40 }}>
            <Col md={6}>
              {imageAsset && (
                <Avatar
                  size={200}
                  css={css`
                    filter: grayscale(100%);
                  `}
                  src={`${imageAsset.fields.file.url}`}
                  alt={imageAsset.fields.title}
                />
              )}
            </Col>

            <Col md={24 - 6}>
              <Typography.Title level={2}>{name}</Typography.Title>

              <Typography.Title level={3}>E-mail</Typography.Title>
              <Typography.Paragraph>{email}</Typography.Paragraph>

              <Typography.Title level={3}>Bio</Typography.Title>
              {documentToReactComponents(bio, documentToRenderOptions)}
            </Col>
          </Row>

          {lecturerCourses.length > 0 && (
            <>
              <Typography.Title level={2} style={{ marginTop: 40 }}>
                {name} a jeho otevřené kurzy.
              </Typography.Title>
              <CoursesList courses={lecturerCourses} />
            </>
          )}
        </SinglePage>
      </Layout>
    </>
  )
}

export default LektoriSlu

interface Props {
  lecturer: Entry<TypeCreatorSkeleton, undefined, string> | null
  lecturerCourses: Entry<TypeProductEntitySkeleton, undefined, string>[]
}

export const getStaticPaths: GetStaticPaths = async (): Promise<GetStaticPathsResult> => {
  const lecturersRes = await contentfulVzClient.getEntries({
    content_type: ContentfulVzContentType.creator
  })

  const paths = (lecturersRes?.items || []).map((l: Entry<TypeCreatorSkeleton, undefined, string>) => ({
    params: {
      slug: l.fields.slug
    }
  }))

  return {
    paths,
    fallback: false
  }
}

export const getStaticProps: GetStaticProps<any, { slug?: string }> = async ({ params: { slug } }): Promise<GetStaticPropsResult<Props>> => {
  try {
    if (!slug) {
      return {
        props: {
          lecturer: null,
          lecturerCourses: []
        },
        revalidate: dev ? 10 : 60
      }
    }

    const lecturerRes = await contentfulVzClient.getEntries<TypeCreatorSkeleton>({
      "fields.slug": slug,
      content_type: ContentfulVzContentType.creator,
      include: 2
    })

    const oneLecturer = lecturerRes?.items?.[0]
    if (!oneLecturer) {
      return {
        props: {
          lecturer: null,
          lecturerCourses: []
        },
        revalidate: dev ? 10 : 60
      }
    }

    const lecturerCourses = await contentfulVzClient.getEntries<TypeProductEntitySkeleton>({
      content_type: ContentfulVzContentType.productEntity,
      include: 2,
      // @ts-ignore
      "fields.creator.sys.id[in]": oneLecturer.sys.id
    })

    return {
      props: {
        lecturer: oneLecturer || null,
        lecturerCourses: lecturerCourses?.items || []
      },
      revalidate: dev ? 10 : 60
    }
  } catch (e) {
    console.error(`Error getEntityBySlug: ${JSON.stringify(e)}`)

    return {
      props: {
        lecturer: null,
        lecturerCourses: []
      },
      revalidate: dev ? 10 : 60
    }
  }
}
